import { putImage } from '@rover/react-lib/src/apis/images.api';
import {
  addImage,
  deleteImage,
  deletePet,
  getDetail,
  getImages,
  memorializePet,
  postOrPut,
  setImageToFirst,
  togglePublicPrivateImage,
} from '@rover/react-lib/src/apis/pet.api';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import { effects as breedsEffects } from '@rover/react-lib/src/redux/ducks/breeds.duck';
import { effects as countryConfigurationEffects } from '@rover/react-lib/src/redux/ducks/countryConfiguration.duck';
import { selectors as frontendConfigurationSelectors } from '@rover/react-lib/src/redux/ducks/frontendConfiguration.duck';
import {
  actions as petActions,
  effects as petEffects,
  selectors as petSelectors,
} from '@rover/react-lib/src/redux/ducks/pets.duck';
import type { DetailedImage, Image, Pet } from '@rover/types';
import { FLAGS } from '@rover/rsdk/src/modules/Data/Core/FrontendConfiguration';
import triggerEmbeddedEvent from '@rover/utilities/embedded';
import { redirect } from '@rover/utilities/url';

import { cleanPetDetails } from './utils';

export type ImageItem = {
  image: DetailedImage;
  isDeleted?: boolean;
  isUpdated?: boolean;
  markForDelete?: boolean;
};
export type State = {
  images: ImageItem[];
  isLoadingImages: boolean;
  loadingImagesErrorMessage: string | null | undefined;
  isSaving: boolean;
  saveErrorMessage: string | null | undefined;
  isUploading: boolean;
  uploadErrorMessage: string | null | undefined;
  uploadedImageUrls: string[];
  petImageListUrl: string;
  isDeletingImages: boolean;
  deleteImagesErrorMessage: string | null | undefined;
  isAddingImages: boolean;
  addImagesErrorMessage: string | null | undefined;
  isDeletingPet: boolean;
  deletePetErrorMessage: string | null | undefined;
  newPetUrl: string;
  isLoading: boolean;
  isHeroUpdated: boolean;
  isLoadingPets: boolean;
  memberUrl: string;
  memberName: string;
  editingPet?: string;
};
type Actions = {
  resetState: ActionCreator<void>;
  addImage: ActionCreator<ImageItem>;
  deleteImage: ActionCreator<any>;
  updateImage: ActionCreator<any>;
  savePetPending: ActionCreator<void>;
  savePetSuccess: ActionCreator<void>;
  savePetFailure: ActionCreator<{
    message: string;
  }>;
  fetchAllPetImagesPending: ActionCreator<void>;
  fetchAllPetImagesSuccess: ActionCreator<Array<Image | DetailedImage>>;
  fetchAllPetImagesFailure: ActionCreator<{
    message: string;
  }>;
  uploadImageSuccess: ActionCreator<string>;
  deleteImagesPending: ActionCreator<void>;
  deleteImagesSuccess: ActionCreator<void>;
  deleteImagesFailure: ActionCreator<{
    message: string;
  }>;
  addImagesPending: ActionCreator<void>;
  addImagesSuccess: ActionCreator<void>;
  addImagesFailure: ActionCreator<{
    message: string;
  }>;
  setHeroImageSuccess: ActionCreator<void>;
  setHeroImageFailure: ActionCreator<{ message: string }>;
  deletePetPending: ActionCreator<void>;
  deletePetSuccess: ActionCreator<void>;
  deletePetFailure: ActionCreator<{
    message: string;
  }>;
  setNewPetUrl: ActionCreator<string>;
  isLoadingToggle: ActionCreator<boolean>;
  updatePetImageListUrl: ActionCreator<string>;
  fetchPetsPending: ActionCreator<void>;
  fetchPetsSuccess: ActionCreator<void>;
  fetchPetsFailure: ActionCreator<void>;
  setMemberUrl: ActionCreator<string>;
  updateMemberName: ActionCreator<string>;
};
type Effects = {
  savePet: EffectCreator<Pet>;
  fetchAllPetImages: EffectCreator<string>;
  fetchAllPets: EffectCreator<string>;
  saveImages: EffectCreator<void>;
  deleteImages: EffectCreator<void>;
  setHeroImage: EffectCreator<Image>;
  updateImagesDetails: EffectCreator<void>;
  refreshData: EffectCreator<void>;
  deletePet: EffectCreator<string>;
  memorializePet: EffectCreator<any>;
  redirectToMemorials: EffectCreator<any>;
  resetData: EffectCreator<string | null | undefined>;
  clearCurrentPet: EffectCreator<void>;
  updateImageDeleteList: EffectCreator<any>;
  addNewImage: EffectCreator<string>;
  heroImageUpdate: EffectCreator<string>;
  togglePublicPrivateImage: EffectCreator<{
    togglePrivacyUrl: string;
    image: DetailedImage;
  }>;
};
type Selectors = {
  getAllImages: SelectorCreator<ImageItem[]>;
  getAllPets: SelectorCreator<Pet[]>;
  getIsLoadingImages: SelectorCreator<boolean>;
  getLoadingImagesErrorMessage: SelectorCreator<string | null | undefined>;
  getIsSaving: SelectorCreator<boolean>;
  getSaveErrorMessage: SelectorCreator<string | null | undefined>;
  getUploadedImageUrls: SelectorCreator<string[]>;
  getPetImageListUrl: SelectorCreator<string>;
  getIsUploading: SelectorCreator<boolean>;
  getUploadErrorMessage: SelectorCreator<string | null | undefined>;
  getIsDeletingImages: SelectorCreator<boolean>;
  getDeleteImagesErrorMessage: SelectorCreator<string | null | undefined>;
  getIsDeletingPet: SelectorCreator<boolean>;
  getDeletePetErrorMessage: SelectorCreator<string | null | undefined>;
  getNewPetUrl: SelectorCreator<string>;
  getIsLoading: SelectorCreator<boolean>;
  getIsHeroUpdated: SelectorCreator<boolean>;
  getAllImagesUrls: SelectorCreator<string[]>;
  getIsLoadingPets: SelectorCreator<boolean>;
  getMemberUrl: SelectorCreator<string>;
  getMemberName: SelectorCreator<string>;
  getMemberOpk: SelectorCreator<string>;
  getShowEditPetInsuranceField: SelectorCreator<boolean>;
};
type Options = DuckOptions<State, Actions, Effects, Selectors>;
const INITIAL_STATE: State = {
  images: [],
  isLoadingImages: false,
  loadingImagesErrorMessage: undefined,
  isSaving: false,
  saveErrorMessage: undefined,
  isUploading: false,
  uploadErrorMessage: undefined,
  uploadedImageUrls: [],
  petImageListUrl: '',
  isDeletingImages: false,
  deleteImagesErrorMessage: undefined,
  isAddingImages: false,
  addImagesErrorMessage: undefined,
  isDeletingPet: false,
  deletePetErrorMessage: undefined,
  newPetUrl: '',
  isLoading: false,
  isHeroUpdated: false,
  editingPet: '',
  isLoadingPets: false,
  memberUrl: '',
  memberName: '',
};
export const options: Options = {
  name: 'editPetProfilePage',
  initialState: INITIAL_STATE,
  actions: {
    resetState: () => () => ({ ...INITIAL_STATE }),
    addImage: (image) => (state) => ({
      ...state,
      images: [...state.images, image],
    }),
    deleteImage: (imageItem: Image) => (state) => ({
      ...state,
      images: state.images.map((currentItem) =>
        currentItem.image.url === imageItem.url ? { ...currentItem, isDeleted: true } : currentItem
      ),
    }),
    updateImage: (imageItem) => (state) => ({
      ...state,
      images: state.images.map((currentItem) =>
        currentItem.image.url === imageItem.url
          ? {
              image: imageItem,
              isUpdated: true,
            }
          : currentItem
      ),
    }),
    fetchAllPetImagesPending: () => (state) => ({ ...state, isLoadingImages: true }),
    fetchAllPetImagesSuccess: (images: Image[] | ImageItem[]) => (state) => ({
      ...state,
      images: images.map((item) => ({
        image: item,
      })),
      isLoadingImages: false,
      loadingImagesErrorMessage: undefined,
    }),
    fetchAllPetImagesFailure:
      ({ message }) =>
      (state) => ({
        ...state,
        images: [],
        isLoadingImages: false,
        loadingImagesErrorMessage: message,
      }),
    savePetPending: () => (state) => ({ ...state, isSaving: true }),
    savePetSuccess: () => (state) => ({ ...state, isSaving: false, saveErrorMessage: undefined }),
    savePetFailure:
      ({ message }) =>
      (state) => ({ ...state, isSaving: false, saveErrorMessage: message }),
    uploadImageSuccess: (uploadedImageUrl) => (state) => ({
      ...state,
      uploadedImageUrls: [...state.uploadedImageUrls, uploadedImageUrl],
    }),
    deleteImagesPending: () => (state) => ({ ...state, isDeletingImages: true }),
    deleteImagesSuccess: () => (state) => ({
      ...state,
      isDeletingImages: false,
      deleteImagesErrorMessage: undefined,
    }),
    deleteImagesFailure:
      ({ message }) =>
      (state) => ({ ...state, isDeletingImages: false, deleteImagesErrorMessage: message }),
    addImagesPending: () => (state) => ({ ...state, isAddingImages: true }),
    addImagesSuccess: () => (state) => ({
      ...state,
      isAddingImages: false,
      addImagesErrorMessage: undefined,
    }),
    addImagesFailure:
      ({ message }) =>
      (state) => ({ ...state, isAddingImages: false, addImagesErrorMessage: message }),
    setHeroImageSuccess: () => (state) => ({ ...state, isHeroUpdated: true }),
    setHeroImageFailure:
      ({ message }) =>
      (state) => ({
        ...state,
        isSettingHeroImage: false,
        setHeroImageErrorMessage: message,
      }),
    deletePetPending: () => (state) => ({ ...state, isDeletingPet: true }),
    deletePetSuccess: () => (state) => ({
      ...state,
      isDeletingPet: false,
      deletePetErrorMessage: undefined,
    }),
    deletePetFailure:
      ({ message }) =>
      (state) => ({ ...state, isDeletingPet: false, deletePetErrorMessage: message }),
    setNewPetUrl: (url) => (state) => ({ ...state, newPetUrl: url }),
    isLoadingToggle: (loading) => (state) => ({ ...state, isLoading: loading }),
    updatePetImageListUrl: (petImageListUrl) => (state) => ({ ...state, petImageListUrl }),
    fetchPetsPending: () => (state) => ({ ...state, isLoadingPets: true }),
    fetchPetsSuccess: () => (state) => ({ ...state, isLoadingPets: false }),
    fetchPetsFailure: () => (state) => ({ ...state, isLoadingPets: false }),
    setMemberUrl: (memberUrl: string) => (state) => ({ ...state, memberUrl }),
    updateMemberName: (memberName: string) => (state) => ({ ...state, memberName }),
  },
  effects: {
    fetchAllPetImages: (imageListUrl) => async (dispatch, getState, actions) => {
      dispatch(actions.fetchAllPetImagesPending());

      try {
        let currentImageListUrl = imageListUrl;
        const totalResults: Array<Image | DetailedImage> = [];

        do {
          // eslint-disable-next-line no-await-in-loop
          const { results, next } = await getImages(currentImageListUrl);
          totalResults.push(...results);
          currentImageListUrl = next as unknown as string;
        } while (currentImageListUrl);

        dispatch(actions.fetchAllPetImagesSuccess(totalResults));
      } catch (e: any) {
        dispatch(actions.fetchAllPetImagesFailure(e));
      }
    },
    fetchAllPets: (memberUrl: string) => async (dispatch, getState, duckActions) => {
      dispatch(duckActions.fetchPetsPending());

      try {
        // todo: find a solution because we need to wait until we get value
        // moved from default db to replica
        setTimeout(async () => {
          const memberName = await dispatch(petEffects.fetchMemberPets(memberUrl));
          dispatch(duckActions.fetchPetsSuccess());

          if (memberUrl) {
            dispatch(duckActions.updateMemberName(memberName as unknown as string));
          }
        }, 1000);
      } catch (e) {
        // error handled in pet duck we need only a loading here
        dispatch(duckActions.fetchPetsFailure());
      }
    },
    deletePet:
      (petUrl) =>
      async (dispatch, getState, actions): Promise<boolean> => {
        dispatch(actions.deletePetPending());

        try {
          await deletePet(petUrl);
          dispatch(petActions.deletePetSuccess(petUrl));
          dispatch(actions.deletePetSuccess());
          return true;
        } catch (e: any) {
          dispatch(actions.deletePetFailure(e));
          return false;
        }
      },
    memorializePet:
      (memorializationData) =>
      async (dispatch, getState, actions): Promise<boolean> => {
        try {
          await memorializePet(memorializationData.petUrl, memorializationData.memorialDate);
          dispatch(petActions.deletePetSuccess(memorializationData.petUrl));
          dispatch(actions.deletePetSuccess());
          return true;
        } catch (e: any) {
          dispatch(actions.deletePetFailure(e));
          return false;
        }
      },
    redirectToMemorials: (redirectUrl) => async (dispatch, getState, actions) => {
      dispatch(actions.deletePetPending());
      redirect(redirectUrl);
    },
    /* Due to this effect returning a value, the typechecker is difficult to satisfy.
      The refactor is out of scope for this TS conversion ticket */
    // @ts-expect-error
    savePet:
      (pet: Pet) =>
      async (
        dispatch,
        getState,
        actions,
        { saveImages, deleteImages, setHeroImage, updateImagesDetails },
        { getDeleteImagesErrorMessage, getNewPetUrl, getIsHeroUpdated }
      ): Promise<Pet | null | undefined> => {
        dispatch(actions.savePetPending());

        try {
          const cleanedPet = cleanPetDetails(pet);
          const url = getNewPetUrl(getState());
          cleanedPet.url = cleanedPet.url || url;
          // Save pet
          const savedPet: Pet = await postOrPut(pet);
          // for embedded web, indicate that we need to refresh the pet profile
          triggerEmbeddedEvent('pet-added-modified');

          if (!savedPet) {
            throw new Error(petSelectors.getSavePetErrorMessage(getState()));
          }

          dispatch(actions.setNewPetUrl(savedPet.url));
          dispatch(actions.updatePetImageListUrl(savedPet?.urls?.images));
          // Delete any images marked for deletion
          await dispatch(deleteImages());
          const deleteErrorMessage = getDeleteImagesErrorMessage(getState());

          if (deleteErrorMessage) {
            throw new Error(deleteErrorMessage);
          }

          // update available images
          await dispatch(updateImagesDetails());
          await dispatch(saveImages());
          // upload hero
          const isUpdatedHeroImage = getIsHeroUpdated(getState());

          if (isUpdatedHeroImage) {
            const heroImage = cleanedPet.defaultImage;
            await dispatch(setHeroImage(heroImage));
          }

          // update pet in list
          const updatedPet = await getDetail(savedPet.url);
          dispatch(petActions.updatePetList(updatedPet));
          dispatch(actions.savePetSuccess());
          return savedPet;
        } catch (e: any) {
          dispatch(actions.savePetFailure(e));
          return null;
        }
      },
    saveImages:
      () =>
      /* Due to this effect returning a value, the typechecker is difficult to satisfy.
      The refactor is out of scope for this TS conversion ticket */
      // @ts-expect-error
      async (
        dispatch,
        getState,
        duckActions,
        effects,
        { getUploadedImageUrls, getPetImageListUrl, getAllImages }
      ): Promise<Image[]> => {
        dispatch(duckActions.addImagesPending());

        try {
          const allImages = getAllImages(getState());
          const allDeletedImageUrls = allImages.map((i) => i.isDeleted && i.image.url);
          const uploadedImagesUrls = getUploadedImageUrls(getState());
          const petImageListUrl = getPetImageListUrl(getState());
          // filter only uploaded images that were not removed meanwhile
          const images = allImages.filter(
            (i) =>
              uploadedImagesUrls.includes(i.image.url) && !allDeletedImageUrls.includes(i.image.url)
          );
          const addedImages = await Promise.all(
            images.map((i) => addImage(petImageListUrl, i.image.url, i.image.caption))
          );
          dispatch(duckActions.addImagesSuccess());
          return addedImages;
        } catch (error: any) {
          dispatch(duckActions.addImagesFailure(error));
          return [];
        }
      },
    deleteImages:
      () =>
      async (dispatch, getState, duckActions, effects, { getAllImages }): Promise<boolean> => {
        dispatch(duckActions.deleteImagesPending());

        try {
          let imageItems = getAllImages(getState());

          if (imageItems) {
            // call delete api only for those images who are not in memory-only
            imageItems = imageItems.filter((img) => img.isDeleted === true && img.image.urls);
          }

          let attemptedDelete = false;
          await Promise.all(
            imageItems.map(async (imageItem) => {
              attemptedDelete = true;
              await deleteImage(imageItem.image.url);
            })
          );
          dispatch(duckActions.deleteImagesSuccess());
          return attemptedDelete;
        } catch (error: any) {
          dispatch(duckActions.deleteImagesFailure(error));
          return false;
        }
      },
    setHeroImage:
      (image) =>
      async (dispatch, getState, duckActions, effects, { getPetImageListUrl }) => {
        try {
          const { url } = image;
          const petImageListUrl = getPetImageListUrl(getState());
          const savedHero = await addImage(petImageListUrl, url);
          await setImageToFirst(savedHero.urls.ordering);
        } catch (error: any) {
          dispatch(duckActions.setHeroImageFailure(error));
        }
      },
    resetData: (petUrl) => async (dispatch, getState, duckActions) => {
      try {
        // if no url clear pet state
        // do not change the order
        if (!petUrl) {
          dispatch(petActions.clearPets());
        }

        // reset current duck state
        dispatch(duckActions.resetState());
        dispatch(duckActions.isLoadingToggle(true));
        await dispatch(countryConfigurationEffects.fetchConfiguration());
        await dispatch(petEffects.fetchPetDetailsOptions());
        await dispatch(breedsEffects.fetchAllBreeds());

        // url wait for pet info fetch
        if (petUrl) {
          await dispatch(petEffects.fetchPetInformation(petUrl));
        }

        dispatch(duckActions.isLoadingToggle(false));
      } catch (error: any) {
        dispatch(duckActions.addImagesFailure(error));
      }
    },
    updateImageDeleteList: (image) => (dispatch, getState, duckActions) => {
      dispatch(duckActions.deleteImage(image));
    },
    addNewImage: (imageUrl) => async (dispatch, getState, duckActions) => {
      dispatch(duckActions.uploadImageSuccess(imageUrl));
      // add preview but do not save until form submit
      dispatch(
        duckActions.addImage({
          image: {
            url: imageUrl,
            medium: imageUrl,
          } as DetailedImage,
        })
      );
    },
    heroImageUpdate: (imageUrl) => async (dispatch, getState, duckActions) => {
      dispatch(
        petActions.updateHeroImage({
          url: imageUrl,
          wide: imageUrl,
        } as Image)
      );
      // add preview but do not save until form submit
      dispatch(duckActions.setHeroImageSuccess());
    },
    updateImagesDetails:
      () =>
      async (
        dispatch,
        getState,
        { addImagesPending, addImagesSuccess, addImagesFailure },
        duckEffects,
        duckSelectors
      ) => {
        dispatch(addImagesPending());

        try {
          const images = duckSelectors.getAllImages(getState());
          // update all images marked for update
          await Promise.all(
            images.map(async (item) => {
              if (!item.isDeleted && item.isUpdated && item.image && item.image.added) {
                await putImage(item.image);
              }
            })
          );
          dispatch(addImagesSuccess());
        } catch (err: any) {
          dispatch(addImagesFailure(err));
        }
      },
    refreshData: () => async (dispatch, getState, actions, effects, selectors) => {
      const member = selectors.getMemberUrl(getState());
      await dispatch(petActions.clearPets());
      await dispatch(effects.fetchAllPets(member));
    },
    clearCurrentPet: () => async (dispatch) => {
      await dispatch(petActions.clearPet());
    },
    togglePublicPrivateImage:
      ({ togglePrivacyUrl, image }) =>
      async (dispatch, getState, actions, effects, selectors) => {
        dispatch(actions.fetchAllPetImagesPending());

        try {
          await togglePublicPrivateImage(togglePrivacyUrl, image.id);
          // note: update in the web duck private/public flag so we do not fetch photo again
          // just for button display re-render
          const allImages = selectors.getAllImages(getState());
          const updatedImages = allImages.map((e) =>
            e.image.url === image.url ? { ...image, public: !image.public } : e.image
          );
          dispatch(actions.fetchAllPetImagesSuccess(updatedImages));
        } catch (e: any) {
          dispatch(actions.fetchAllPetImagesFailure(e));
        }
      },
  },
  selectors: {
    getAllImages: (getState, createSelector) => createSelector([getState], (s) => s.images),
    getAllPets: (getState, createSelector) =>
      createSelector([getState, petSelectors.getPets], (state, pets) => pets),
    getIsLoadingImages: (getState, createSelector) =>
      createSelector([getState], (s) => s.isLoadingImages),
    getLoadingImagesErrorMessage: (getState, createSelector) =>
      createSelector([getState], (s) => s.loadingImagesErrorMessage),
    getIsSaving: (getState, createSelector) => createSelector([getState], (s) => s.isSaving),
    getSaveErrorMessage: (getState, createSelector) =>
      createSelector([getState], (s) => s.saveErrorMessage),
    getIsUploading: (getState, createSelector) => createSelector([getState], (s) => s.isUploading),
    getUploadErrorMessage: (getState, createSelector) =>
      createSelector([getState], (s) => s.uploadErrorMessage),
    getUploadedImageUrls: (getState, createSelector) =>
      createSelector([getState], (s) => s.uploadedImageUrls),
    getPetImageListUrl: (getState, createSelector) =>
      createSelector([getState], (s) => s.petImageListUrl),
    getIsDeletingImages: (getState, createSelector) =>
      createSelector([getState], (s) => s.isDeletingImages),
    getDeleteImagesErrorMessage: (getState, createSelector) =>
      createSelector([getState], (s) => s.deleteImagesErrorMessage),
    getIsDeletingPet: (getState, createSelector) =>
      createSelector([getState], (s) => s.isDeletingPet),
    getDeletePetErrorMessage: (getState, createSelector) =>
      createSelector([getState], (s) => s.deletePetErrorMessage),
    getNewPetUrl: (getState, createSelector) => createSelector([getState], (s) => s.newPetUrl),
    getIsLoading: (getState, createSelector) => createSelector([getState], (s) => s.isLoading),
    getAllImagesUrls: (getState, createSelector, { getAllImages }) =>
      createSelector([getAllImages], (images) => images.map((img) => img.image.medium)),
    getIsHeroUpdated: (getState, createSelector) =>
      createSelector([getState], (s) => s.isHeroUpdated),
    getIsLoadingPets: (getState, createSelector) =>
      createSelector([getState], (s) => s.isLoadingPets),
    getMemberUrl: (getState, createSelector) => createSelector([getState], (s) => s.memberUrl),
    getMemberName: (getState, createSelector) => createSelector([getState], (s) => s.memberName),
    getMemberOpk: (getState, createSelector) =>
      createSelector(
        [getState],
        (s) =>
          s.memberUrl &&
          (s.memberUrl
            .substr(0, s.memberUrl.length - 1)
            .split('/')
            .pop() as string)
      ),
    getShowEditPetInsuranceField: (getState, createSelector) =>
      createSelector([frontendConfigurationSelectors.isFlagActive], (isFlagActive) =>
        isFlagActive(FLAGS.ROLLOUT_EDIT_PET_INSURANCE_FIELD)
      ),
  },
};
export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
